import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import type { Dispatch, SetStateAction } from "react"

import type { KnowledgeNode } from "../types"
import type { UseKnowledgeBaseReturn } from "./useKnowledgeBase"

const AUTO_EXPAND_DELAY_MS = 600
const DRAG_FILE_TYPES = new Set(["Files", "application/x-moz-file"])

const isFileDrag = (e: React.DragEvent<HTMLElement>) => {
  const types = Array.from(e.dataTransfer.types || [])
  return types.some((type) => DRAG_FILE_TYPES.has(type))
}

const isLeavingElement = (e: React.DragEvent<HTMLElement>) => {
  const related = e.relatedTarget
  if (!(related instanceof Node)) return true
  return !e.currentTarget.contains(related)
}

const isRealFolderNode = (node: KnowledgeNode) => {
  return (
    node.node_type === "FOLDER" &&
    node.ui_kind !== "WORKSPACE" &&
    !node.id.startsWith("kb-explorer:")
  )
}

const isSyntheticRootNode = (node: KnowledgeNode) => {
  return node.id === "kb-explorer:root"
}

export interface UseKnowledgeBaseDropUploadParams {
  kb: UseKnowledgeBaseReturn
  currentFolderNode: KnowledgeNode
  openedFileId: string | null
  expandedNodes: Record<string, boolean>
  setExpandedNodes: Dispatch<SetStateAction<Record<string, boolean>>>
  onUploadSuccess?: (folderId: string, files: File[]) => void
  onUploadFailure?: (errorKey: string) => void
}

export interface UseKnowledgeBaseDropUploadReturn {
  isDraggingTreePanel: boolean
  isDraggingDetailPanel: boolean
  hoveredTreeNodeId: string | null
  activeTreeDropTargetId: string | null
  dropErrorKey: string | null
  clearDropErrorKey: () => void
  treePanelDragHandlers: {
    onDragOver: (e: React.DragEvent<HTMLElement>) => void
    onDragLeave: (e: React.DragEvent<HTMLElement>) => void
    onDrop: (e: React.DragEvent<HTMLElement>) => void
  }
  detailPanelDragHandlers: {
    onDragOver: (e: React.DragEvent<HTMLElement>) => void
    onDragLeave: (e: React.DragEvent<HTMLElement>) => void
    onDrop: (e: React.DragEvent<HTMLElement>) => void
  }
  getTreeNodeDragHandlers: (node: KnowledgeNode) => {
    onDragEnter: (e: React.DragEvent<HTMLElement>) => void
    onDragOver: (e: React.DragEvent<HTMLElement>) => void
    onDragLeave: (e: React.DragEvent<HTMLElement>) => void
    onDrop: (e: React.DragEvent<HTMLElement>) => void
  }
  isTreeDropAllowed: (node: KnowledgeNode) => boolean
  resolveUploadFolderIdFromTreeNode: (
    node: KnowledgeNode
  ) => Promise<string | null>
  detailDropOverlayState: boolean
  isDetailDropAllowed: boolean
}

const getDetailDropDisabledErrorKey = (
  node: KnowledgeNode,
  openedFileId: string | null
): string => {
  if (openedFileId !== null) {
    return "knowledgeBase.treeView.dropDisabledInPreview"
  }

  if (isSyntheticRootNode(node)) {
    return "knowledgeBase.treeView.dropDisabledInHome"
  }

  if (node.ui_kind === "WORKSPACE") {
    return "knowledgeBase.treeView.dropDisabledInWorkspace"
  }

  return "knowledgeBase.treeView.dropTargetInvalid"
}

const getTreeDropInvalidErrorKey = (node: KnowledgeNode): string => {
  if (isSyntheticRootNode(node)) {
    return "knowledgeBase.treeView.dropDisabledInHome"
  }

  if (node.ui_kind === "WORKSPACE") {
    return "knowledgeBase.treeView.dropNoFolderTarget"
  }

  return "knowledgeBase.treeView.dropTargetInvalid"
}

export const useKnowledgeBaseDropUpload = ({
  kb,
  currentFolderNode,
  openedFileId,
  expandedNodes,
  setExpandedNodes,
  onUploadSuccess,
  onUploadFailure,
}: UseKnowledgeBaseDropUploadParams): UseKnowledgeBaseDropUploadReturn => {
  const [isDraggingTreePanel, setIsDraggingTreePanel] = useState(false)
  const [isDraggingDetailPanel, setIsDraggingDetailPanel] = useState(false)
  const [hoveredTreeNodeId, setHoveredTreeNodeId] = useState<string | null>(
    null
  )
  const [activeTreeDropTargetId, setActiveTreeDropTargetId] = useState<
    string | null
  >(null)
  const [dropErrorKey, setDropErrorKey] = useState<string | null>(null)
  const autoExpandTimerRef = useRef<number | null>(null)

  const clearAutoExpandTimer = useCallback(() => {
    if (autoExpandTimerRef.current !== null) {
      window.clearTimeout(autoExpandTimerRef.current)
      autoExpandTimerRef.current = null
    }
  }, [])

  const reportDropError = useCallback(
    (errorKey: string) => {
      setDropErrorKey(errorKey)
      onUploadFailure?.(errorKey)
    },
    [onUploadFailure]
  )

  const resolveUploadFolderIdFromTreeNode = useCallback(
    async (node: KnowledgeNode): Promise<string | null> => {
      if (isRealFolderNode(node)) {
        return node.id
      }

      if (node.node_type === "FILE") {
        return node.parent_id || null
      }

      return null
    },
    []
  )

  const resolveUploadFolderIdFromTreeNodeSync = useCallback(
    (node: KnowledgeNode): string | null => {
      if (isRealFolderNode(node)) return node.id
      if (node.node_type === "FILE") return node.parent_id || null
      return null
    },
    []
  )

  const isTreeDropAllowed = useCallback((node: KnowledgeNode) => {
    if (node.node_type === "FILE") return !!node.parent_id
    return isRealFolderNode(node)
  }, [])

  const uploadFiles = useCallback(
    async (folderId: string | null, files: File[]) => {
      if (!folderId) {
        reportDropError("knowledgeBase.treeView.dropTargetInvalid")
        return
      }
      if (files.length === 0) return
      try {
        await kb.addFiles(folderId, files)
        onUploadSuccess?.(folderId, files)
      } catch (error) {
        console.error("[KB] drop upload failed", error)
        reportDropError("knowledgeBase.treeView.dropTargetInvalid")
      }
    },
    [kb, onUploadSuccess, reportDropError]
  )

  const scheduleAutoExpand = useCallback(
    (node: KnowledgeNode) => {
      clearAutoExpandTimer()
      const shouldExpand =
        node.node_type === "FOLDER" &&
        (node.ui_kind === "WORKSPACE" || isRealFolderNode(node)) &&
        !expandedNodes[node.id]

      if (!shouldExpand) return

      autoExpandTimerRef.current = window.setTimeout(() => {
        void kb.ensureChildrenLoaded(node.id).then(async () => {
          const resolvedFolderId = await resolveUploadFolderIdFromTreeNode(node)
          setActiveTreeDropTargetId(resolvedFolderId)
        })
        setExpandedNodes((prev) => {
          if (prev[node.id]) return prev
          return { ...prev, [node.id]: true }
        })
        autoExpandTimerRef.current = null
      }, AUTO_EXPAND_DELAY_MS)
    },
    [
      clearAutoExpandTimer,
      expandedNodes,
      kb,
      resolveUploadFolderIdFromTreeNode,
      setExpandedNodes,
    ]
  )

  const resetTreeDragState = useCallback(() => {
    setIsDraggingTreePanel(false)
    setHoveredTreeNodeId(null)
    setActiveTreeDropTargetId(null)
    clearAutoExpandTimer()
  }, [clearAutoExpandTimer])

  const resetDetailDragState = useCallback(() => {
    setIsDraggingDetailPanel(false)
  }, [])

  const handleDropOnTreeNode = useCallback(
    async (node: KnowledgeNode, e: React.DragEvent<HTMLElement>) => {
      e.preventDefault()
      e.stopPropagation()
      clearAutoExpandTimer()

      if (!isFileDrag(e)) {
        resetTreeDragState()
        return
      }

      const files = Array.from(e.dataTransfer.files)
      if (files.length === 0) {
        resetTreeDragState()
        return
      }

      const folderId = await resolveUploadFolderIdFromTreeNode(node)
      if (!folderId) {
        reportDropError(getTreeDropInvalidErrorKey(node))
        resetTreeDragState()
        return
      }

      await uploadFiles(folderId, files)
      resetTreeDragState()
    },
    [
      clearAutoExpandTimer,
      reportDropError,
      resetTreeDragState,
      resolveUploadFolderIdFromTreeNode,
      uploadFiles,
    ]
  )

  const getTreeNodeDragHandlers = useCallback(
    (node: KnowledgeNode) => ({
      onDragEnter: (e: React.DragEvent<HTMLElement>) => {
        if (!isFileDrag(e)) return
        e.preventDefault()
        e.stopPropagation()
        setIsDraggingTreePanel(true)
        setHoveredTreeNodeId(node.id)
        setActiveTreeDropTargetId(resolveUploadFolderIdFromTreeNodeSync(node))
        scheduleAutoExpand(node)
      },
      onDragOver: (e: React.DragEvent<HTMLElement>) => {
        if (!isFileDrag(e)) return
        e.preventDefault()
        e.stopPropagation()
        setIsDraggingTreePanel(true)
        setActiveTreeDropTargetId(resolveUploadFolderIdFromTreeNodeSync(node))
        if (hoveredTreeNodeId !== node.id) {
          setHoveredTreeNodeId(node.id)
          scheduleAutoExpand(node)
        }
      },
      onDragLeave: (e: React.DragEvent<HTMLElement>) => {
        if (!isFileDrag(e)) return
        e.preventDefault()
        e.stopPropagation()
        if (!isLeavingElement(e)) return
        if (hoveredTreeNodeId === node.id) {
          setHoveredTreeNodeId(null)
        }
        setActiveTreeDropTargetId(null)
        clearAutoExpandTimer()
      },
      onDrop: (e: React.DragEvent<HTMLElement>) => {
        void handleDropOnTreeNode(node, e)
      },
    }),
    [
      clearAutoExpandTimer,
      handleDropOnTreeNode,
      hoveredTreeNodeId,
      resolveUploadFolderIdFromTreeNodeSync,
      scheduleAutoExpand,
    ]
  )

  const treePanelDragHandlers = useMemo(
    () => ({
      onDragOver: (e: React.DragEvent<HTMLElement>) => {
        if (!isFileDrag(e)) return
        e.preventDefault()
        setIsDraggingTreePanel(true)
      },
      onDragLeave: (e: React.DragEvent<HTMLElement>) => {
        if (!isFileDrag(e)) return
        e.preventDefault()
        if (!isLeavingElement(e)) return
        resetTreeDragState()
      },
      onDrop: (e: React.DragEvent<HTMLElement>) => {
        if (!isFileDrag(e)) return
        e.preventDefault()
        e.stopPropagation()
        resetTreeDragState()
      },
    }),
    [resetTreeDragState]
  )

  const isDetailDropAllowed =
    openedFileId === null && isRealFolderNode(currentFolderNode)

  const detailPanelDragHandlers = useMemo(
    () => ({
      onDragOver: (e: React.DragEvent<HTMLElement>) => {
        if (!isFileDrag(e)) return
        e.preventDefault()
        if (!isDetailDropAllowed) return
        setIsDraggingDetailPanel(true)
      },
      onDragLeave: (e: React.DragEvent<HTMLElement>) => {
        if (!isFileDrag(e)) return
        e.preventDefault()
        if (!isLeavingElement(e)) return
        resetDetailDragState()
      },
      onDrop: (e: React.DragEvent<HTMLElement>) => {
        if (!isFileDrag(e)) return
        e.preventDefault()
        e.stopPropagation()

        const files = Array.from(e.dataTransfer.files)
        if (files.length === 0) {
          resetDetailDragState()
          return
        }

        if (!isDetailDropAllowed) {
          reportDropError(
            getDetailDropDisabledErrorKey(currentFolderNode, openedFileId)
          )
          resetDetailDragState()
          return
        }

        void uploadFiles(currentFolderNode.id, files).finally(() => {
          resetDetailDragState()
        })
      },
    }),
    [
      currentFolderNode,
      isDetailDropAllowed,
      openedFileId,
      reportDropError,
      resetDetailDragState,
      uploadFiles,
    ]
  )

  useEffect(() => {
    return () => {
      clearAutoExpandTimer()
    }
  }, [clearAutoExpandTimer])

  return {
    isDraggingTreePanel,
    isDraggingDetailPanel,
    hoveredTreeNodeId,
    activeTreeDropTargetId,
    dropErrorKey,
    clearDropErrorKey: () => setDropErrorKey(null),
    treePanelDragHandlers,
    detailPanelDragHandlers,
    getTreeNodeDragHandlers,
    isTreeDropAllowed,
    resolveUploadFolderIdFromTreeNode,
    detailDropOverlayState: isDraggingDetailPanel && isDetailDropAllowed,
    isDetailDropAllowed,
  }
}

export default useKnowledgeBaseDropUpload
